import { useNavigate } from "react-router-dom";
import { FaBuilding, FaPiggyBank, FaFileSignature, FaShieldAlt, FaHandshake, FaGlobe, FaMoneyBillWave, FaCheck, FaTimes } from "react-icons/fa";
import "./GettingStartedShared.css";

const General6 = () => {
    const navigate = useNavigate();

    const relatedArticles = [
         {
            title: "How the USA Incorporation Process Works",
            description: "A step-by-step guide to incorporating your business in the United States.",
            link: "/how-the-usa-incorporation-process-word"
        },
        {
            title: "6 reasons why your startup should be incorporated in the US",
            description: "Discover the key benefits of incorporating your startup in the United States.",
            link: "/why-should-your-startup-incorporate-in-the-usa"
        },
        {
            title: "What Are the Ongoing Compliance Requirements After US Incorporation?",
            description: "Understand the legal and tax obligations for maintaining your US business entity.",
            link: "/what-are-the-ongoing-compliance-requirements-after-us-incorporation?"
        }
    ];

    const bankingOptions = [
        {
            icon: <FaBuilding className="gs-feature-icon" />,
            title: "Traditional US Banks",
            description: "Banks like Chase, Bank of America, and Wells Fargo offer business accounts but typically require a US address and SSN/ITIN.",
            pros: ["Widely accepted", "Physical branch access", "Full banking services"],
            cons: ["Strict requirements", "May require US presence", "Higher fees for international transactions"]
        },
        {
            icon: <FaGlobe className="gs-feature-icon" />,
            title: "Online-Only Banks",
            description: "Neobanks like Mercury, Relay, and Novo cater to startups and international businesses.",
            pros: ["Easier approval", "Lower fees", "Designed for remote businesses"],
            cons: ["No physical branches", "Limited cash deposits", "Customer service limitations"]
        },
        {
            icon: <FaHandshake className="gs-feature-icon" />,
            title: "EMI (Electronic Money Institutions)",
            description: "Services like Wise, Payoneer, and Revolut offer multi-currency business accounts.",
            pros: ["Multi-currency support", "Lower FX fees", "Easy international transfers"],
            cons: ["Not a full bank", "Limits on transactions", "May have holding periods"]
        }
    ];

    return (
        <div className="gs-container">
            <header className="gs-header">
                <div className="gs-container-1200">
                </div>
            </header>
            
            <main className="gs-main-content">
                <div className="gs-container-1200">
                    <nav className="gs-breadcrumb">
                        <ol>
                            <li><a href="/help">Help Center</a></li>
                            <li><a href="/getting-started">Getting Started</a></li>
                            <li>General</li>
                        </ol>
                    </nav>

                    <article className="gs-article">
                        <header className="gs-article-header">
                            <h1 className="gs-article-title">Opening a US Business Bank Account as a Non-Resident: What Are Your Options?</h1>
                            <div className="gs-article-meta">
                                <span>Posted by BooksComply Team</span>
                                <span>•</span>
                                <span>Last updated August 2025</span>
                            </div>
                        </header>
                        
                        <div className="gs-article-content">
                            <div className="gs-intro-card">
                                <FaPiggyBank className="gs-intro-icon" />
                                <p>
                                    Once your US company is incorporated and you've obtained your EIN, the next important
                                    step is setting up a US business bank account. This enables you to accept payments,
                                    manage expenses, and build business credibility.
                                    But for non-US residents, this process can be tricky. Here's a clear breakdown of your
                                    banking options, documentation requirements, and how to open an account—even if
                                    you're based outside the US.
                                </p>
                            </div>

                            <section className="gs-section">
                                <h2>Why You Need a US Business Bank Account</h2>
                                <div className="gs-feature-grid">
                                    <div className="gs-feature-card">
                                        <FaMoneyBillWave className="gs-feature-icon" />
                                        <h3>Accept Payments</h3>
                                        <p>From US customers, vendors, and platforms like PayPal, Stripe, Amazon, and Shopify.</p>
                                    </div>
                                    <div className="gs-feature-card">
                                        <FaShieldAlt className="gs-feature-icon" />
                                        <h3>Legal Protection</h3>
                                        <p>Separate personal and business finances to maintain liability protection.</p>
                                    </div>
                                    <div className="gs-feature-card">
                                        <FaFileSignature className="gs-feature-icon" />
                                        <h3>Professionalism</h3>
                                        <p>Build credibility with US-based clients and partners.</p>
                                    </div>
                                </div>
                            </section>

                            <section className="gs-section">
                                <h2>Banking Options for Non-Residents</h2>
                                <div className="gs-options-grid">
                                    {bankingOptions.map((option, index) => (
                                        <div key={index} className="gs-option-card">
                                            <div className="gs-option-header">
                                                <div className="gs-option-icon">
                                                    {option.icon}
                                                </div>
                                                <h3>{option.title}</h3>
                                            </div>
                                            <p>{option.description}</p>
                                            <div className="gs-pros-cons">
                                                <div className="gs-pros">
                                                    <h4>Pros</h4>
                                                    <ul>
                                                        {option.pros.map((pro, i) => (
                                                            <li key={`pro-${i}`}><FaCheck className="gs-check" /> {pro}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div className="gs-cons">
                                                    <h4>Cons</h4>
                                                    <ul>
                                                        {option.cons.map((con, i) => (
                                                            <li key={`con-${i}`}><FaTimes className="gs-times" /> {con}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            <section className="gs-section">
                                <h2>Required Documents</h2>
                                <div className="gs-docs-grid">
                                    <div className="gs-doc-card">
                                        <FaFileSignature className="gs-doc-icon" />
                                        <h3>Business Documents</h3>
                                        <ul>
                                            <li>Articles of Incorporation/Organization</li>
                                            <li>EIN Confirmation Letter (CP575 or 147C)</li>
                                            <li>Certificate of Good Standing</li>
                                            <li>Ownership structure documentation</li>
                                        </ul>
                                    </div>
                                    <div className="gs-doc-card">
                                        <FaBuilding className="gs-doc-icon" />
                                        <h3>Personal Documents</h3>
                                        <ul>
                                            <li>Government-issued ID (passport, driver's license)</li>
                                            <li>Proof of address (utility bill, bank statement)</li>
                                            <li>Business license (if applicable)</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            <div className="gs-cta-section">
                                <h2>Need Help with US Banking?</h2>
                                <p>
                                    At BooksComply, we specialize in helping international entrepreneurs navigate US banking. Our services include:
                                </p>
                                <ul className="gs-feature-list">
                                    <li>Bank account opening support</li>
                                    <li>Document preparation and verification</li>
                                    <li>Introduction to banking partners</li>
                                    <li>Ongoing compliance and account management</li>
                                </ul>
                                <a href="/contact" className="gs-primary-btn">Contact Us for Assistance</a>
                            </div>

                            {/* <div className="gs-helpful-section">
                                <h3 className="gs-helpful-title">Was this article helpful?</h3>
                                <div className="gs-helpful-buttons">
                                    <button className="gs-helpful-btn positive">
                                        <FaCheckCircle /> Yes, it was helpful
                                    </button>
                                    <button className="gs-helpful-btn negative">
                                        <FaTimesCircle /> No, I need more help
                                    </button>
                                </div>
                            </div> */}

                            <div className="gs-related-articles">
                                <h2 className="gs-related-title">Related Articles</h2>
                                <div className="gs-related-grid">
                                    {relatedArticles.map((article, index) => (
                                        <a 
                                            key={index} 
                                            href={article.link} 
                                            className="gs-related-card"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(article.link);
                                            }}
                                        >
                                            <h3>{article.title}</h3>
                                            <p>{article.description}</p>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </article>
                </div>
            </main>
        </div>
    );
};

export default General6;